/**
 * Vouchers workspace (add-voucher-system).
 *
 * Create batches of prepaid QR vouchers, browse them grouped by batch with
 * live spend, disable single vouchers or whole batches, re-print a batch's A4
 * sheet, and run the end-of-event sweep. Spend figures come straight from the
 * server's reconciliation read (live strichliste balances) — see
 * accounting.md §19.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Collapse,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import type { ColumnsType, ColumnType } from "antd/es/table";
import { Link } from "react-router-dom";
import {
  ApiError,
  api,
  type ExpenseCategoryRow,
  type VoucherListResponse,
  type VoucherRow,
  type VoucherSummary,
} from "../../api/client";
import { QuickFilterDropdown } from "../../components/QuickFilterDropdown";
import { VOUCHERS_PER_PAGE } from "./constants";

const euro = (c?: number | null) =>
  c == null ? "—" : "€" + (c / 100).toFixed(2);

// DD.MM HH:MM — the admin-table timestamp baseline.
const ts = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// The six headline figures — rendered identically for the grand total (top
// of the page) and per batch (each accordion header), so a batch row reads
// exactly like the total. Caller wraps these spans in a <Space>.
function summaryItems(sum: VoucherSummary) {
  return (
    <>
      <span>
        <b>{sum.redeemed}</b> redeemed
      </span>
      <span>
        <b>{sum.unused}</b> unused
      </span>
      <span>
        <b>{sum.disabled}</b> disabled
      </span>
      <span>Handed out: {euro(sum.face_out_cents)}</span>
      <span>Spent: {euro(sum.spent_cents)}</span>
      <span>Unspent: {euro(sum.unspent_cents)}</span>
    </>
  );
}

function redeemerTag(row: VoucherRow) {
  if (!row.redeemed_by_strichliste_user_id) return null;
  if (row.swept) return <Tag color="default">swept</Tag>;
  if (row.redeemer_type === "voucher") return <Tag color="blue">voucher</Tag>;
  return <Tag color="green">converted</Tag>;
}

export function VouchersPage() {
  const [data, setData] = useState<VoucherListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<ExpenseCategoryRow[]>([]);
  const [batchOptions, setBatchOptions] = useState<number[]>([]);
  const [maxFaceCents, setMaxFaceCents] = useState<number | undefined>();

  // Filters (server-side).
  const [fCreation, setFCreation] = useState<number | undefined>();
  const [fCategory, setFCategory] = useState<number | undefined>();
  const [fStatus, setFStatus] = useState<string | undefined>();
  const [fRedeemer, setFRedeemer] = useState<string | undefined>();
  const [fHideUnused, setFHideUnused] = useState(false);
  const [fQ, setFQ] = useState("");

  // View: batches grouped in a single-open accordion, or one flat sortable/
  // filterable table of all vouchers.
  const [view, setView] = useState<"grouped" | "flat">("grouped");
  const [openBatch, setOpenBatch] = useState<number | undefined>(undefined);

  // Create modal.
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<{
    prefix: string;
    amount_cents: number;
    expense_category_id: number;
    print_amount: boolean;
    auto_close: boolean;
    allow_convert: boolean;
    batch_comment: string;
    pages: number;
  }>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listVouchers({
        creation_id: fCreation,
        category_id: fCategory,
        status: fStatus,
        redeemer_type: fRedeemer,
        hide_unused: fHideUnused,
        q: fQ || undefined,
      });
      setData(res);
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : "Failed to load vouchers");
    } finally {
      setLoading(false);
    }
  }, [fCreation, fCategory, fStatus, fRedeemer, fHideUnused, fQ]);

  useEffect(() => {
    void load();
  }, [load]);

  const refreshBatchOptions = useCallback(() => {
    api
      .listVouchers({})
      .then((r) => {
        const ids = Array.from(
          new Set(r.vouchers.map((v) => v.creation_id)),
        ).sort((a, b) => b - a);
        setBatchOptions(ids);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    api
      .listExpenseCategories({})
      .then(setCategories)
      .catch(() => {});
    api
      .voucherConfig()
      .then((c) => setMaxFaceCents(c.max_face_cents))
      .catch(() => {});
    refreshBatchOptions();
  }, [refreshBatchOptions]);

  // Re-fetch expense categories whenever the create dialog opens, so a category
  // created elsewhere in the same SPA session (no full reload) is selectable.
  useEffect(() => {
    if (modalOpen) {
      api.listExpenseCategories({}).then(setCategories).catch(() => {});
    }
  }, [modalOpen]);

  const groups = useMemo(() => {
    const m = new Map<number, VoucherRow[]>();
    for (const v of data?.vouchers ?? []) {
      if (!m.has(v.creation_id)) m.set(v.creation_id, []);
      m.get(v.creation_id)!.push(v);
    }
    return Array.from(m.entries()).sort((a, b) => b[0] - a[0]);
  }, [data]);

  // Default all batches collapsed. Keep the user's open one across reloads if it
  // still exists; otherwise collapse everything (accordion = at most one open).
  useEffect(() => {
    setOpenBatch((cur) =>
      cur != null && groups.some(([id]) => id === cur) ? cur : undefined,
    );
  }, [groups]);

  // `first` = the original print opened right after creating the batch (no
  // reprint marker). The Reprint button omits it, so the print page marks it.
  const openPrint = (creationId: number, first = false) =>
    window.open(
      `/vouchers/batch/${creationId}/print${first ? "?first=1" : ""}`,
      "_blank",
      "noopener",
    );

  const submitCreate = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setCreating(true);
    try {
      const res = await api.createVoucherBatch({
        prefix: values.prefix.trim(),
        amount_cents: values.amount_cents,
        expense_category_id: values.expense_category_id,
        print_amount: values.print_amount,
        auto_close: values.auto_close,
        allow_convert: values.allow_convert,
        batch_comment: values.batch_comment?.trim() || null,
        pages: values.pages,
      });
      message.success(`Created Batch #${res.creation_id} (${res.count} vouchers)`);
      setModalOpen(false);
      form.resetFields();
      refreshBatchOptions();
      await load();
      openPrint(res.creation_id, true);
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : "Failed to create batch");
    } finally {
      setCreating(false);
    }
  };

  const disableOne = (row: VoucherRow) => {
    Modal.confirm({
      title: `Disable voucher ${row.code}?`,
      content:
        "A disabled voucher can no longer be redeemed. This does not move any money.",
      okText: "Disable",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await api.disableVouchers({ ids: [row.id] });
          await load();
        } catch (e) {
          message.error(e instanceof ApiError ? e.message : "Failed to disable");
        }
      },
    });
  };

  const disableBatch = (creationId: number) => {
    Modal.confirm({
      title: `Disable all un-redeemed vouchers in Batch #${creationId}?`,
      content:
        "Only un-redeemed vouchers are affected; already-redeemed users keep their credit.",
      okText: "Disable batch",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const r = await api.disableVouchers({ creation_id: creationId });
          message.success(`Disabled ${r.disabled} voucher(s)`);
          await load();
        } catch (e) {
          message.error(e instanceof ApiError ? e.message : "Failed to disable batch");
        }
      },
    });
  };

  const runSweep = () => {
    const scope = fCreation;
    Modal.confirm({
      title: "End-of-event sweep",
      width: 520,
      content: (
        <div>
          <p style={{ color: "#b91c1c", fontWeight: 600 }}>
            This disables un-redeemed vouchers and reclaims leftover balances
            from voucher users back to the voucher account. It cannot be undone.
          </p>
          <p>
            {scope != null
              ? `Scope: Batch #${scope} only.`
              : "Scope: ALL voucher users + all un-redeemed vouchers."}
          </p>
        </div>
      ),
      okText: "Run sweep",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const r = await api.sweepVouchers({ creation_id: scope });
          message.success(
            `Swept ${r.swept_accounts} account(s), reclaimed ${euro(
              r.reclaimed_cents,
            )}; disabled ${r.disabled_unused} unused`,
          );
          await load();
        } catch (e) {
          message.error(e instanceof ApiError ? e.message : "Sweep failed");
        }
      },
    });
  };

  // Retrospectively toggle a behaviour flag — per voucher (ids) or per batch
  // (creation_id).
  const setFlag = async (
    sel: { ids?: number[]; creation_id?: number },
    patch: { auto_close?: boolean; allow_convert?: boolean; batch_comment?: string | null },
  ) => {
    try {
      await api.setVoucherFlags({ ...sel, ...patch });
      await load();
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : "Failed to update flags");
    }
  };

  // --- shared cell renderers (used by both the grouped + flat tables) ---
  const statusTag = (st: string) =>
    st === "redeemed" ? (
      <Tag color="green">redeemed</Tag>
    ) : st === "disabled" ? (
      <Tag color="red">disabled</Tag>
    ) : (
      <Tag>unused</Tag>
    );

  const redeemerCell = (row: VoucherRow) =>
    row.redeemed_by_strichliste_user_id ? (
      <Space size={4}>
        <Link to={`/users/${row.redeemed_by_strichliste_user_id}`}>
          {row.redeemer_name || `#${row.redeemed_by_strichliste_user_id}`}
        </Link>
        {redeemerTag(row)}
        {row.redeemer_voucher_count > 1 ? (
          <Tag color="gold">×{row.redeemer_voucher_count}</Tag>
        ) : null}
      </Space>
    ) : (
      <Typography.Text type="secondary">—</Typography.Text>
    );

  const spentCell = (row: VoucherRow) => {
    if (row.spent_cents == null) return euro(null);
    // Multi-redeem: spend is account-level (can't be split per card), so show
    // "shared" with the account total in a tooltip rather than repeating an
    // over-face figure on each card row.
    if (row.redeemer_voucher_count > 1) {
      return (
        <Tooltip
          title={`${euro(row.spent_cents)} spent across ${row.redeemer_voucher_count} vouchers (shared account)`}
        >
          <Typography.Text
            type="secondary"
            style={{ borderBottom: "1px dotted #999", cursor: "help" }}
          >
            shared
          </Typography.Text>
        </Tooltip>
      );
    }
    return euro(row.spent_cents);
  };

  const convertSwitch = (row: VoucherRow) => (
    <Tooltip
      title={
        row.allow_convert
          ? "Holder may convert to a guest tab when overspent"
          : "Hard cap — no guest conversion on overspend"
      }
    >
      <Switch
        size="small"
        checked={row.allow_convert}
        onChange={(v) => setFlag({ ids: [row.id] }, { allow_convert: v })}
      />
    </Tooltip>
  );

  const autoCloseSwitch = (row: VoucherRow) => (
    <Tooltip title="Disable the account once its balance is spent to €0">
      <Switch
        size="small"
        checked={row.auto_close}
        onChange={(v) => setFlag({ ids: [row.id] }, { auto_close: v })}
      />
    </Tooltip>
  );

  const disableCell = (row: VoucherRow) =>
    row.disabled ? null : (
      <Button size="small" danger onClick={() => disableOne(row)}>
        Disable
      </Button>
    );

  // Compact per-batch table columns (the batch is the accordion panel).
  const columns: ColumnsType<VoucherRow> = [
    { title: "Code", dataIndex: "code", key: "code" },
    { title: "Value", dataIndex: "amount_cents", key: "amount_cents", render: (c: number) => euro(c) },
    { title: "Status", dataIndex: "status", key: "status", render: (st: string) => statusTag(st) },
    { title: "Redeemed by", key: "redeemer", render: (_: unknown, row: VoucherRow) => redeemerCell(row) },
    { title: "Spent", key: "spent_cents", render: (_: unknown, row: VoucherRow) => spentCell(row) },
    { title: "Convert", key: "allow_convert", render: (_: unknown, row: VoucherRow) => convertSwitch(row) },
    { title: "Auto-close", key: "auto_close", render: (_: unknown, row: VoucherRow) => autoCloseSwitch(row) },
    { title: "", key: "actions", render: (_: unknown, row: VoucherRow) => disableCell(row) },
  ];

  // Free-text column filter (case-insensitive substring) — the Users/Catalogue
  // text-column pattern.
  const textFilter = (
    label: string,
    getVal: (r: VoucherRow) => string,
  ): Partial<ColumnType<VoucherRow>> => ({
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          autoFocus
          placeholder={`Search ${label}`}
          value={(selectedKeys[0] as string) ?? ""}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ width: 200, marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button size="small" type="primary" onClick={() => confirm()}>
            Search
          </Button>
          <Button size="small" onClick={() => { clearFilters?.(); confirm(); }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    onFilter: (value, record) =>
      getVal(record).toLowerCase().includes(String(value).toLowerCase()),
  });

  // On/Off single-click filter for the boolean flag columns.
  const yesNoFilter = (
    get: (r: VoucherRow) => boolean,
  ): Partial<ColumnType<VoucherRow>> => ({
    filterDropdown: (p) => (
      <QuickFilterDropdown
        props={p}
        options={[
          { value: "yes", label: "On" },
          { value: "no", label: "Off" },
        ]}
      />
    ),
    onFilter: (value, record) => (get(record) ? "yes" : "no") === value,
  });

  // Flat table: every voucher, all columns, client-side sort + per-column filter
  // (matches the Users / Catalogue column UX).
  const flatColumns: ColumnsType<VoucherRow> = [
    {
      title: "Batch",
      dataIndex: "creation_id",
      key: "creation_id",
      width: 90,
      sorter: (a, b) => a.creation_id - b.creation_id,
      defaultSortOrder: "descend",
      filterDropdown: (p) => (
        <QuickFilterDropdown
          props={p}
          options={batchOptions.map((id) => ({ value: String(id), label: `#${id}` }))}
        />
      ),
      onFilter: (value, record) => String(record.creation_id) === value,
      render: (id: number) => <>#{id}</>,
    },
    {
      title: "Comment",
      dataIndex: "batch_comment",
      key: "batch_comment",
      ellipsis: true,
      sorter: (a, b) =>
        (a.batch_comment || "").localeCompare(b.batch_comment || ""),
      ...textFilter("comment", (r) => r.batch_comment || ""),
      render: (c: string | null) =>
        c ? c : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      sorter: (a, b) => a.code.localeCompare(b.code),
      ...textFilter("code", (r) => r.code),
      render: (code: string) => (
        <span style={{ fontFamily: "monospace" }}>{code}</span>
      ),
    },
    {
      title: "Value",
      dataIndex: "amount_cents",
      key: "amount_cents",
      width: 100,
      align: "right",
      sorter: (a, b) => a.amount_cents - b.amount_cents,
      render: (c: number) => euro(c),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      sorter: (a, b) => a.status.localeCompare(b.status),
      filterDropdown: (p) => (
        <QuickFilterDropdown
          props={p}
          options={[
            { value: "unused", label: "Unused" },
            { value: "redeemed", label: "Redeemed" },
            { value: "disabled", label: "Disabled" },
          ]}
        />
      ),
      onFilter: (value, record) => record.status === value,
      render: (st: string) => statusTag(st),
    },
    {
      title: "Redeemed by",
      key: "redeemer",
      sorter: (a, b) =>
        (a.redeemer_name || "").localeCompare(b.redeemer_name || ""),
      ...textFilter("redeemer", (r) => r.redeemer_name || ""),
      render: (_: unknown, row: VoucherRow) => redeemerCell(row),
    },
    {
      title: "Spent",
      key: "spent_cents",
      width: 100,
      align: "right",
      sorter: (a, b) => (a.spent_cents ?? -1) - (b.spent_cents ?? -1),
      render: (_: unknown, row: VoucherRow) => spentCell(row),
    },
    {
      title: "Convert",
      key: "allow_convert",
      width: 100,
      ...yesNoFilter((r) => r.allow_convert),
      render: (_: unknown, row: VoucherRow) => convertSwitch(row),
    },
    {
      title: "Auto-close",
      key: "auto_close",
      width: 110,
      ...yesNoFilter((r) => r.auto_close),
      render: (_: unknown, row: VoucherRow) => autoCloseSwitch(row),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 120,
      sorter: (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
      render: (v: string | null) => ts(v),
    },
    {
      title: "",
      key: "actions",
      width: 90,
      render: (_: unknown, row: VoucherRow) => disableCell(row),
    },
  ];

  const s = data?.summary;

  return (
    <div style={{ padding: 16 }}>
      <Space
        style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}
        align="start"
      >
        <Typography.Title level={3} style={{ margin: 0 }}>
          Vouchers
        </Typography.Title>
        <Space>
          <Button danger onClick={runSweep}>
            Sweep…
          </Button>
          <Button type="primary" onClick={() => setModalOpen(true)}>
            Create batch
          </Button>
        </Space>
      </Space>

      {s ? (
        <Space size="large" style={{ marginBottom: 16 }} wrap>
          {summaryItems(s)}
        </Space>
      ) : null}

      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder="Batch"
          style={{ width: 140 }}
          value={fCreation}
          onChange={(v) => setFCreation(v)}
          options={batchOptions.map((id) => ({ value: id, label: `Batch #${id}` }))}
        />
        <Select
          allowClear
          placeholder="Category"
          style={{ width: 180 }}
          value={fCategory}
          onChange={(v) => setFCategory(v)}
          options={categories.map((c) => ({ value: c.id, label: c.name }))}
        />
        <Select
          allowClear
          placeholder="Status"
          style={{ width: 140 }}
          value={fStatus}
          onChange={(v) => setFStatus(v)}
          options={[
            { value: "unused", label: "Unused" },
            { value: "redeemed", label: "Redeemed" },
            { value: "disabled", label: "Disabled" },
          ]}
        />
        <Select
          allowClear
          placeholder="Redeemer"
          style={{ width: 150 }}
          value={fRedeemer}
          onChange={(v) => setFRedeemer(v)}
          options={[
            { value: "voucher", label: "Voucher user" },
            { value: "converted", label: "Converted" },
          ]}
        />
        <Input.Search
          allowClear
          placeholder="Search code"
          style={{ width: 200 }}
          onSearch={(v) => setFQ(v)}
          onChange={(e) => {
            if (!e.target.value) setFQ("");
          }}
        />
        <Space size={4}>
          <Switch checked={fHideUnused} onChange={setFHideUnused} />
          <span>Hide unused</span>
        </Space>
        <Segmented
          value={view}
          onChange={(v) => setView(v as "grouped" | "flat")}
          options={[
            { label: "Batches", value: "grouped" },
            { label: "Flat list", value: "flat" },
          ]}
        />
      </Space>

      {view === "grouped" ? (
        <Collapse
          accordion
          // Only the front expand-arrow toggles a panel — so clicking the header
          // (incl. editing the batch comment or flipping a flag switch) never
          // expands/collapses it.
          collapsible="icon"
          activeKey={openBatch != null ? [String(openBatch)] : []}
          onChange={(k) => {
            const arr = Array.isArray(k) ? k : k ? [k] : [];
            setOpenBatch(arr.length ? Number(arr[arr.length - 1]) : undefined);
          }}
          items={groups.map(([creationId, rows]) => ({
            key: String(creationId),
            label: (
              <Space wrap size={[12, 4]}>
                <b>Batch #{creationId}</b>
                <Typography.Text type="secondary">
                  {rows[0]?.prefix} · {euro(rows[0]?.amount_cents)}
                </Typography.Text>
                {/* Same six figures as the grand total at the top, scoped to
                    this batch (server-computed in batch_summaries so the
                    shared-account spend logic matches exactly). */}
                {data?.batch_summaries?.[String(creationId)] ? (
                  <Space wrap size={12} style={{ fontSize: 13 }}>
                    {summaryItems(data.batch_summaries[String(creationId)])}
                  </Space>
                ) : null}
                {/* Inline-editable batch comment; stopPropagation so clicking to
                    edit doesn't toggle the panel. */}
                <span
                  onClick={(e) => e.stopPropagation()}
                  style={{ marginLeft: 12 }}
                >
                  <Typography.Text
                    type={rows[0]?.batch_comment ? undefined : "secondary"}
                    italic={!rows[0]?.batch_comment}
                    editable={{
                      text: rows[0]?.batch_comment ?? "",
                      maxLength: 255,
                      tooltip: "Edit batch comment",
                      onChange: (val) =>
                        setFlag({ creation_id: creationId }, { batch_comment: val }),
                    }}
                  >
                    {rows[0]?.batch_comment || "add comment…"}
                  </Typography.Text>
                </span>
              </Space>
            ),
            // stopPropagation so toggling a control doesn't collapse the panel.
            extra: (
              <Space wrap onClick={(e) => e.stopPropagation()}>
                <Tooltip title="Allow guest conversion when overspent — whole batch (off = hard cap)">
                  <span style={{ fontSize: 12 }}>
                    Convert{" "}
                    <Switch
                      size="small"
                      checked={rows.every((r) => r.allow_convert)}
                      onChange={(v) =>
                        setFlag({ creation_id: creationId }, { allow_convert: v })
                      }
                    />
                  </span>
                </Tooltip>
                <Tooltip title="Auto-close accounts when spent to €0 — whole batch">
                  <span style={{ fontSize: 12 }}>
                    Auto-close{" "}
                    <Switch
                      size="small"
                      checked={rows.every((r) => r.auto_close)}
                      onChange={(v) =>
                        setFlag({ creation_id: creationId }, { auto_close: v })
                      }
                    />
                  </span>
                </Tooltip>
                <Button size="small" onClick={() => openPrint(creationId)}>
                  Reprint
                </Button>
                <Button
                  size="small"
                  danger
                  onClick={() => disableBatch(creationId)}
                >
                  Disable unused
                </Button>
              </Space>
            ),
            children: (
              <Table<VoucherRow>
                rowKey="id"
                size="small"
                loading={loading}
                dataSource={rows}
                columns={columns}
                pagination={false}
              />
            ),
          }))}
        />
      ) : (
        <Table<VoucherRow>
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={data?.vouchers ?? []}
          columns={flatColumns}
          sticky
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: [25, 50, 100, 200],
            showTotal: (t) => `${t} vouchers`,
          }}
        />
      )}

      {view === "grouped" && !loading && groups.length === 0 ? (
        <Typography.Text type="secondary">No vouchers found.</Typography.Text>
      ) : null}

      <Modal
        title="Create voucher batch"
        open={modalOpen}
        confirmLoading={creating}
        onOk={submitCreate}
        okText="Create"
        onCancel={() => setModalOpen(false)}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            print_amount: true,
            allow_convert: true,
            auto_close: false,
            batch_comment: "",
            pages: 1,
            amount_cents: 500,
          }}
        >
          <Form.Item
            name="prefix"
            label="Prefix"
            rules={[
              { required: true, message: "Prefix is required" },
              {
                pattern: /^[A-Za-z0-9]+$/,
                message: "Letters and digits only (no spaces or dashes)",
              },
            ]}
            extra="Codes look like 2026-<prefix>-123456"
          >
            <Input placeholder="helfer" />
          </Form.Item>
          <Form.Item
            name="batch_comment"
            label="Batch comment (optional)"
            extra="A note to identify this batch — editable later."
          >
            <Input placeholder="e.g. Helfer shift 1" maxLength={255} />
          </Form.Item>
          <Form.Item
            name="amount_cents"
            label="Value per voucher (cents)"
            extra={
              maxFaceCents != null
                ? `Max ${maxFaceCents} ct (€${(maxFaceCents / 100).toFixed(2)}) per voucher`
                : undefined
            }
            rules={[
              { required: true, message: "Value is required" },
              { type: "number" as const, min: 1, message: "Value must be positive" },
              ...(maxFaceCents != null
                ? [
                    {
                      type: "number" as const,
                      max: maxFaceCents,
                      message: `Max ${maxFaceCents} ct per voucher`,
                    },
                  ]
                : []),
            ]}
          >
            <InputNumber
              min={1}
              max={maxFaceCents}
              step={50}
              style={{ width: "100%" }}
              addonAfter="ct"
            />
          </Form.Item>
          <Form.Item
            name="expense_category_id"
            label="Expense category"
            rules={[{ required: true, message: "Category is required" }]}
          >
            <Select
              placeholder="Select a category"
              options={categories.map((c) => ({ value: c.id, label: c.name }))}
            />
          </Form.Item>
          <Form.Item
            name="pages"
            label={`A4 pages (× ${VOUCHERS_PER_PAGE} per page)`}
            rules={[{ required: true, message: "Page count is required" }]}
          >
            <InputNumber min={1} max={200} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="print_amount" label="Print value on voucher" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="allow_convert"
            label="Allow guest conversion when overspent"
            valuePropName="checked"
            extra="Off = hard cap: an overspend is blocked with no conversion to a guest tab."
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="auto_close"
            label="Auto-close the account once fully spent (€0)"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default VouchersPage;
